import React, {useState, useRef, useEffect} from "react";
import {
    Button,
    ButtonType,
    Checkbox,
    InlineType,
    NumberInput,
    TextAreaInput
} from "@renta-apps/athenaeum-react-components";
import {FileModel} from "@renta-apps/athenaeum-toolkit";
import RentaToolsConstants from "@/helpers/RentaToolsConstants";
import ReturnedMassProduct from "@/models/server/ReturnedMassProduct";
import {UploadFullImageResponse} from "@/models/UploadFullImageResponse";
import Localizer from "@/localization/Localizer";

import styles from "./MassProductItem.module.scss";
import Show from "@/components/Show/Show";
import rentaToolsStyles from "@/pages/RentaTools.module.scss";
import UnleashHelper from "@/helpers/UnleashHelper";
import {FeatureSwitch} from "@/providers/FeatureSwitch";
import ToolsImageInput from "@/components/ToolsImageInput/ToolsImageInput";

interface IMassProductItemProps {
    massProduct: ReturnedMassProduct;
    preview?: boolean;
    onChange?(massProduct: ReturnedMassProduct): Promise<void>;
    onImageUpload?(picture: FileModel): Promise<UploadFullImageResponse | null>;
}

const MassProductItem: React.FC<IMassProductItemProps> = ({
                                                              massProduct,
                                                              preview = false,
                                                              onChange,
                                                              onImageUpload,
                                                          }) => {
    const [massProductState, setMassProductState] = useState(massProduct);
    const [expanded, setExpanded] = useState(false);
    const [invoice, setInvoice] = useState(false);
    const [includePictures, setIncludePictures] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const _brokenItemsRef = useRef<NumberInput>(null);
    const _invoicedItemsRef = useRef<NumberInput>(null);

    useEffect(() => {
        if(massProduct.pictures) {
            massProduct.pictures?.length > 0 ? setIncludePictures(true) : setIncludePictures(false);
        }

        if(massProduct.brokenAmount > 0) {
            setInvoice(true);
        }
    }, []);


    const isInvoiceable = () => {
        return (massProduct.invoicedAmount ?? 0)> 0 ||massProduct.brokenAmount > 0;
    }

    const toggleInvoiceAsync = async (): Promise<void> => {
        if (invoice) {
            massProduct.invoicedAmount = null;

            await updateMassProductStateAsync();
        }

        setInvoice(!invoice);
    }

    const toggleCommentAsync = async (): Promise<void> => {
        setExpanded(!expanded);
    }

    const toggleIncludePicturesAsync = async (): Promise<void> => {
        if (!includePictures) {
            massProduct.pictures = null;
        }

        setIncludePictures(!includePictures);
    }

    const increaseBrokenItemsAsync = async (): Promise<void> => {
        if (_brokenItemsRef.current) {
            await _brokenItemsRef.current.increaseAsync();
        }
    }

    const decreaseBrokenItemsAsync = async (): Promise<void> => {
        if (_brokenItemsRef.current) {
            await _brokenItemsRef.current.decreaseAsync();
        }
    }

    const increaseInvoicedItemsAsync = async (): Promise<void> => {
        if (_invoicedItemsRef.current) {
            await _invoicedItemsRef.current.increaseAsync();
        }
    }

    const decreaseInvoicedItemsAsync = async (): Promise<void> => {
        if (_invoicedItemsRef.current) {
            await _invoicedItemsRef.current.decreaseAsync();

            if (_invoicedItemsRef.current.value == 0) {
                await resetInvoiceAsync();
            }
        }
    }

    const onChangeBrokenAmountAsync = async (brokenAmount: number): Promise<void> => {
        if (massProduct.brokenAmount !== brokenAmount) {
            massProduct.brokenAmount = brokenAmount;

            if (massProduct.invoicedAmount != null && massProduct.invoicedAmount > massProduct.brokenAmount) {
                massProduct.invoicedAmount = massProduct.brokenAmount;
            }

            if(massProduct.brokenAmount > 0){
                setInvoice(true);
            }

            if (massProduct.brokenAmount == 0) {
                await resetInvoiceAsync();
            }

            await updateMassProductStateAsync();
        }
    }

    const onChangeInvoiceAmountAsync = async (invoiceAmount: number, userInteraction: boolean): Promise<void> => {
        if (userInteraction && invoiceAmount === 0) {
            await resetInvoiceAsync();
        } else {
            massProduct.invoicedAmount = invoiceAmount;
        }

        await updateMassProductStateAsync();
    }

    const onChangeCommentAsync = async (comment: string): Promise<void> => {
        if (massProduct.comment !== comment) {
            massProduct.comment = comment;

            await updateMassProductStateAsync();
        }
    }

    const onChangeImageAsync = async (pictures: FileModel[]): Promise<void> => {
        massProduct.pictures = pictures;

        if(onChange) {
            await onChange(massProduct)
        }
        await updateMassProductStateAsync();
    }

    const handleImageUploadAsync = async (image: FileModel): Promise<FileModel | null> => {
        if (onImageUpload) {
            setIsLoading(true);

            const response: UploadFullImageResponse | null = await onImageUpload(image);

            setIsLoading(false);

            if (!response) {
                return null;
            }

            return response.fileModel;
        }

        return null;
    }

    const onInvoicedChange = (value : boolean) => {
        massProduct.invoiced = value;

        setMassProductState({...massProductState});
    }

    const resetInvoiceAsync = async (): Promise<void> => {
        massProduct.invoicedAmount = null;

        setInvoice(false);
    }

    const updateMassProductStateAsync = async (): Promise<void> => {
        setMassProductState({...massProductState});
    };

    const renderBrokenItemsContent = () => (
        <>
            <div className={styles.customInputLabel}>
                <span>{Localizer.massProductItemBrokenItems}</span>
            </div>

            <div id={"brokenItemsContent"} className={styles.content}>

                <div className={styles.actions}>
                    <div>
                        <Button block
                                id={"minusBrokenItems"}
                                disabled={preview}
                                type={ButtonType.Orange}
                                icon={{name: "fas minus"}}
                                onClick={() => decreaseBrokenItemsAsync()}
                        />
                    </div>
                </div>

                <div id={"brokenItemsDataContainer"} className={styles.data}>

                    <div id={"itemBrokenItemsInputWrapper"} className={styles.input}>
                        <NumberInput hideArrows clickToEdit
                                     id="brokenItemsAmountInput"
                                     ref={_brokenItemsRef}
                                     readonly={preview}
                                     disabled={preview}
                                     min={0}
                                     max={massProduct.returnedAmount}
                                     step={1}
                                     format={"0"}
                                     value={massProduct.brokenAmount}
                                     onChange={(_, amount) => onChangeBrokenAmountAsync(amount)}
                        />
                    </div>

                </div>

                <div className={styles.actions}>

                    <div>
                        <Button block
                                id={"plusBrokenItems"}
                                disabled={preview}
                                type={ButtonType.Orange}
                                icon={{name: "fas plus"}}
                                onClick={() => increaseBrokenItemsAsync()}
                        />

                    </div>

                </div>
            </div>
        </>
    );

    const renderInvoicedItemsContent = () => (
        <FeatureSwitch flagName={RentaToolsConstants.featureFlagMassProductInvoicing}>
            <div className={styles.customInputLabel}>
                <span>{Localizer.massProductItemToBeInvoiced}</span>
            </div>

            <div id={"invoicedItemsContent"} className={styles.content}>
                <div className={styles.actions}>
                    <div>
                        <Button block
                                id={"minusInvoicedItems"}
                                disabled={preview}
                                type={ButtonType.Orange}
                                icon={{name: "fas minus"}}
                                onClick={() => decreaseInvoicedItemsAsync()}
                        />
                    </div>
                </div>

                <div id={"invoicedItemsDataContainer"} className={styles.data}>
                    <div id={"itemInvoicedItemsInputWrapper"} className={styles.input}>
                        <NumberInput hideArrows clickToEdit
                                     id="invoicedItemsAmountInput"
                                     ref={_invoicedItemsRef}
                                     readonly={preview}
                                     disabled={preview}
                                     min={0}
                                     max={massProduct.brokenAmount}
                                     step={1}
                                     format={"0"}
                                     value={massProduct.invoicedAmount}
                                     onChange={(_, amount, userInteraction) => onChangeInvoiceAmountAsync(amount, userInteraction)}
                        />
                    </div>

                </div>

                <div className={styles.actions}>

                    <div>
                        <Button block
                                id={"plusInvoicedItems"}
                                disabled={preview}
                                type={ButtonType.Orange}
                                icon={{name: "fas plus"}}
                                onClick={() => increaseInvoicedItemsAsync()}
                        />

                    </div>

                </div>
            </div>
        </FeatureSwitch>
    );

    const renderItemPictures = () => (
        <>
            <div className={styles.picture}>
                <div className={styles.pictureLabel}>
                    {Localizer.genericAddPictures.toUpperCase()}
                </div>

                <ToolsImageInput multi noAutoSelect
                                 noEdit={UnleashHelper.isEnabled(RentaToolsConstants.featureFlagDirectImageDownloads)}
                                 readonly={preview}
                                 pictures={massProduct.pictures}
                                 className="flex-1"
                                 onChange={(pictures: FileModel[]) => onChangeImageAsync(pictures)}
                                 maxImageRequestSizeInBytes={RentaToolsConstants.originalImageMaxSize}
                                 convertImage={(image: FileModel) => handleImageUploadAsync(image)}
                                 fileTypes={[...RentaToolsConstants.supportedImageFileTypes]}
                                 imageUrl={(image: FileModel) => image.src}

                />
            </div>
        </>
    );

    const massProductName: string = massProduct.name || "*";
    const massProductItemId: string = `massProductItem_${massProductName.replace(/\s/g, "_")}`;

    const commentButtonIconName: string = (massProduct.comment)
        ? "far comment-alt-dots"
        : "far comment-alt";

    const invoiceIconName: string = (invoice)
        ? "fas trash"
        : "fas file-invoice";

    const invoiceIconType: ButtonType = (invoice)
        ? ButtonType.Danger
        : ButtonType.Success;

    const includePicturesLabel: string = (!includePictures)
        ? Localizer.genericAddPictures.toUpperCase()
        : Localizer.genericRemoveAllPictures.toUpperCase();

    const includePicturesIconName: string = (includePictures)
        ? "fas trash"
        : "fas camera";

    const includePicturesType: ButtonType = (includePictures)
        ? ButtonType.Danger
        : ButtonType.Orange;

    return (
        <div className={"w-100"}>
            <div id={massProductItemId}
                 className={styles.massProductItem}>

                <div id={"itemHeader"} className={styles.title}>

                    <div id={"itemName"} className={styles.name}>
                            <span>
                                {massProductName}
                            </span>
                    </div>

                    <div id={"returnedAmount"} className={styles.name}>
                            <span>
                                {Localizer.massProductItemReturnedAmount.format(massProduct.returnedAmount)}
                            </span>
                    </div>

                </div>

                {renderBrokenItemsContent()}

                {
                    (invoice && massProduct.brokenAmount > 0) &&
                    (
                        renderInvoicedItemsContent()
                    )
                }

                <div className={"mt-2 mb-3"}>
                    <div className={"float-left"}>
                        <Button block
                                id={"itemInvoicedToggler"}
                                minWidth={"60px"}
                                type={invoiceIconType}
                                disabled={(massProduct.brokenAmount <= 0 || preview)}
                                icon={{name: invoiceIconName}}
                                onClick={() => toggleInvoiceAsync()}
                        />
                    </div>

                    <div className={"float-right"}>
                        <Button block
                                id={"itemCommentToggler"}
                                minWidth={"60px"}
                                type={ButtonType.Orange}
                                disabled={(massProduct.returnedAmount <= 0 || preview)}
                                icon={{name: commentButtonIconName}}
                                onClick={() => toggleCommentAsync()}
                        />
                    </div>
                </div>

                <div id={"itemComment"} className={styles.comment}>
                            <span>
                                {massProduct.comment}
                            </span>
                </div>

                <div className={styles.commentInput}>
                    {
                        (expanded) &&
                        (
                            <TextAreaInput autoFocus
                                           id={"itemCommentInput"}
                                           readonly={preview}
                                           value={massProduct.comment || ""}
                                           onChange={(_, value) => onChangeCommentAsync(value)}
                            />
                        )
                    }
                </div>

                <div>
                    <Button block
                            id={"includePicturesToggler"}
                            className={"mt-2"}
                            disabled={preview}
                            type={includePicturesType}
                            label={includePicturesLabel}
                            icon={{name: includePicturesIconName}}
                            onClick={() => toggleIncludePicturesAsync()}
                    />
                </div>
            </div>

            {
                (includePictures) &&
                (
                    renderItemPictures()
                )
            }

            <FeatureSwitch flagName={RentaToolsConstants.featureFlagMassProductInvoicing}>
                <Show.When isTrue={isInvoiceable()}>
                    <div className={rentaToolsStyles.arsenalCheckboxes}>
                        <Checkbox inline
                                readonly={preview}
                                label={Localizer.fuelingAndWashingInvoiceWashing}
                                inlineType={InlineType.Right}
                                value={massProduct.invoiced}
                                onChange={async (sender, value) => onInvoicedChange(value)}
                        />
                    </div>
                </Show.When>
            </FeatureSwitch>
        </div>
    );
};

export default MassProductItem;